import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Save, RefreshCw, Utensils, Soup, AlertCircle, Check } from 'lucide-react'
import { useApp } from '../App'
import { orderSourceLabel } from '../lib/cityWorkspace'
import { useToastActions } from '../components/Toast'
import { menusAPI } from '../lib/api'
import { tr } from '../lib/translations'
import ProductionSheet from './ProductionSheet'

// Slot classification — Meal Counts page treats addons (wellness shots,
// date balls) as Sides alongside broth/oats/chia, since the kitchen counts
// them together. Production Sheet still splits addons separately.
const SIDE_CATEGORIES = [
  'broth', 'broth_2', 'oats', 'chia', 'chia_2',
  'wellness', 'wellness_shots', 'date_balls',
]

// ══════════════════════════════════════════════════════════════════════════════
// MEAL COUNT CARD
// ══════════════════════════════════════════════════════════════════════════════

function MealCountCard({ dish, count, onChange }) {
  const { isSpanish, translations } = useApp()
  const categoryColors = {
    chicken: 'bg-green-600',
    chicken_2: 'bg-emerald-700',
    beef: 'bg-red-600',
    seafood: 'bg-cyan-600',
    seafood_2: 'bg-teal-700',
    veg: 'bg-lime-600',
    chef: 'bg-purple-600',
    cheat: 'bg-amber-600',
    pasta: 'bg-stone-500',
    salad: 'bg-emerald-600',
    salad_2: 'bg-emerald-500',
    broth: 'bg-amber-700',
    broth_2: 'bg-amber-500',
    oats: 'bg-yellow-600',
    chia: 'bg-pink-600',
    chia_2: 'bg-pink-400',
    wellness: 'bg-emerald-500',
    wellness_shots: 'bg-emerald-500',
    date_balls: 'bg-amber-800',
  }

  const isSide = SIDE_CATEGORIES.includes(dish.category)
  const displayName = tr(isSpanish, translations, 'dishes', dish.id, 'name', dish.name)

  return (
    <div className="bg-white rounded-xl border border-havn-cream-300 p-4 hover:shadow-soft transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2 h-2 rounded-full ${categoryColors[dish.category] || 'bg-gray-400'}`} />
            <span className="text-xs font-medium text-havn-gray-500 uppercase tracking-wide">
              {dish.category.replace('_', ' ')}
            </span>
          </div>
          <h3 className="font-display text-base font-semibold text-havn-charcoal truncate">
            {displayName}
          </h3>
          {dish.cuisine && (
            <p className="text-xs text-havn-gray-400 mt-0.5">{dish.cuisine}</p>
          )}
        </div>

        <div className="flex flex-col items-end gap-1">
          <input
            type="number"
            value={count}
            onChange={(e) => onChange(parseInt(e.target.value) || 0)}
            onFocus={(e) => e.target.select()}
            className="w-20 h-10 text-center text-lg font-semibold text-havn-green-700 bg-havn-cream-100 border border-havn-cream-300 rounded-lg
                       focus:border-havn-green-700 focus:ring-1 focus:ring-havn-green-700 transition-colors"
            min="0"
          />
          <span className="text-xs text-havn-gray-400">
            {isSide ? 'sides' : 'meals'}
          </span>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════

export default function MealCounts() {
  const { currentMenu, currentCity, isSpanish } = useApp()
  const [menu, setMenu] = useState(null)
  const [mealCounts, setMealCounts] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const { success, error } = useToastActions()
  const autoSaveRef = useRef(null)
  const pendingCountsRef = useRef(null)

  // Load menu data
  useEffect(() => {
    if (!currentMenu) {
      setLoading(false)
      return
    }

    async function loadMenu() {
      try {
        setLoading(true)
        const data = await menusAPI.get(currentMenu)
        setMenu(data)

        // Build meal counts from dishes
        const counts = {}
        data.dishes?.forEach(d => {
          counts[d.dish_id] = d.meal_count || 0
        })
        setMealCounts(counts)
        setHasChanges(false)
      } catch (err) {
        error('Failed to load menu')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    loadMenu()
  }, [currentMenu])

  // Auto-save function
  const performAutoSave = useCallback(async (countsToSave) => {
    if (!currentMenu || !countsToSave) return

    try {
      setSaving(true)
      await menusAPI.updateMealCounts(currentMenu, countsToSave)
      setLastSaved(new Date())
      setHasChanges(false)
    } catch (err) {
      error('Auto-save failed: ' + err.message)
      console.error('Auto-save failed:', err)
    } finally {
      setSaving(false)
    }
  }, [currentMenu, error])

  // Update a meal count with auto-save
  function handleCountChange(dishId, count) {
    const newCounts = { ...mealCounts, [dishId]: count }
    setMealCounts(newCounts)
    setHasChanges(true)
    pendingCountsRef.current = newCounts

    // Debounced auto-save after 1.5 seconds of no changes
    if (autoSaveRef.current) {
      clearTimeout(autoSaveRef.current)
    }
    autoSaveRef.current = setTimeout(() => {
      if (pendingCountsRef.current) {
        performAutoSave(pendingCountsRef.current)
        pendingCountsRef.current = null
      }
    }, 1500)
  }

  // Clean up timeout on unmount and save pending changes
  useEffect(() => {
    return () => {
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current)
        // Save any pending changes to localStorage before unmount
        if (pendingCountsRef.current && currentMenu) {
          localStorage.setItem(
            `havn_pending_meal_counts_${currentMenu}`,
            JSON.stringify({
              counts: pendingCountsRef.current,
              timestamp: Date.now()
            })
          )
        }
      }
    }
  }, [currentMenu])

  // Check for and recover any pending changes on mount
  useEffect(() => {
    if (!currentMenu) return

    const pendingKey = `havn_pending_meal_counts_${currentMenu}`
    const pending = localStorage.getItem(pendingKey)

    if (pending) {
      try {
        const { counts, timestamp } = JSON.parse(pending)
        // Only recover if less than 5 minutes old
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          setMealCounts(counts)
          setHasChanges(true)
          // Clear after loading
          localStorage.removeItem(pendingKey)
          // Auto-save the recovered data
          setTimeout(() => performAutoSave(counts), 500)
        } else {
          localStorage.removeItem(pendingKey)
        }
      } catch (e) {
        localStorage.removeItem(pendingKey)
      }
    }
  }, [currentMenu, performAutoSave])

  // Save changes
  async function handleSave() {
    if (!currentMenu) return

    try {
      setSaving(true)
      await menusAPI.updateMealCounts(currentMenu, mealCounts)

      // Verify save by reloading the data
      const verifyData = await menusAPI.get(currentMenu)

      // Check if at least one value matches what we saved
      let verified = true
      const savedCounts = {}
      verifyData.dishes?.forEach(d => {
        savedCounts[d.dish_id] = d.meal_count
        if (mealCounts[d.dish_id] !== undefined && mealCounts[d.dish_id] !== d.meal_count) {
          verified = false
        }
      })

      if (!verified) {
        console.error('Save verification failed:', { sent: mealCounts, received: savedCounts })
        error('Save may not have completed - please refresh and check')
        return
      }

      success('Meal counts saved')
      setHasChanges(false)
    } catch (err) {
      error('Failed to save: ' + err.message)
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  // Handle imported meal counts from Airtable Production Sheet
  // Maps category totals (chicken: 107, beef: 129, etc.) → dish_id meal counts
  function handleImportMealCounts(importedCounts) {
    if (!menu?.dishes || !importedCounts) {
      return
    }

    const newCounts = { ...mealCounts }
    let updated = 0

    menu.dishes.forEach(dish => {
      // Map dish category → backend meal_counts key.
      // Most categories map 1:1 to themselves; only legacy aliases need translation:
      //   chef → cheat (chef slot was retired; Airtable still uses "Cheat" field)
      //   wellness → wellness_shots (frontend shorthand → backend canonical key)
      const importKey =
        dish.category === 'chef' ? 'cheat' :
        dish.category === 'wellness' ? 'wellness_shots' :
        dish.category
      const imported = importedCounts[importKey]

      if (imported !== undefined) {
        // For meals (chicken/beef/seafood/cheat): use .total (glass + plastic combined)
        // For sides (broth/oats): use .total directly
        const total = typeof imported === 'object' ? (imported.total || 0) : imported
        newCounts[dish.dish_id] = total
        updated++
      }
    })

    if (updated === 0) {
      return
    }

    setMealCounts(newCounts)
    setHasChanges(true)
    pendingCountsRef.current = newCounts

    // Trigger immediate save (don't wait for debounce)
    performAutoSave(newCounts)
    success(`Meal counts updated from ${orderSourceLabel(currentCity)} (${updated} items)`)
  }

  // Group dishes by category.
  // Addons (wellness shots, date balls) render in the Meals section — that's where
  // the kitchen expects to see them. Only broth/oats/chia variants split out as Sides.
  const groupedDishes = useMemo(() => {
    if (!menu?.dishes) return {}

    const groups = { meals: [], sides: [] }

    menu.dishes.forEach(dish => {
      if (SIDE_CATEGORIES.includes(dish.category)) {
        groups.sides.push(dish)
      } else {
        groups.meals.push(dish)
      }
    })

    return groups
  }, [menu])

  // Calculate totals
  // Meals total includes addons (wellness shots, date balls) — kitchen counts
  // them alongside meals on this page.
  const totals = useMemo(() => {
    let meals = 0
    let sides = 0

    Object.entries(mealCounts).forEach(([dishId, count]) => {
      const dish = menu?.dishes?.find(d => d.dish_id === parseInt(dishId))
      if (dish) {
        if (SIDE_CATEGORIES.includes(dish.category)) {
          sides += count
        } else {
          meals += count
        }
      }
    })

    return { meals, sides, total: meals + sides }
  }, [mealCounts, menu])

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-havn-green-700" />
      </div>
    )
  }

  // No menu selected
  if (!currentMenu) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="w-12 h-12 text-havn-gray-300 mb-4" />
        <h3 className="font-display text-lg font-semibold text-havn-gray-500">No Menu Selected</h3>
        <p className="text-sm text-havn-gray-400 mt-1">
          Select or create a menu from the sidebar to enter meal counts
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto animate-fade-in">
      {/* Header with Totals */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Combined Total - Highlighted */}
          <div className="flex items-center gap-3 px-5 py-3 bg-havn-green-700 rounded-xl text-white">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <Utensils className="w-5 h-5" />
            </div>
            <div>
              <div className="text-3xl font-display font-bold">
                {totals.total}
              </div>
              <div className="text-xs text-white/80 uppercase tracking-wide">Total Meals + Sides</div>
            </div>
          </div>

          <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-havn-cream-300">
            <Utensils className="w-5 h-5 text-havn-green-700" />
            <div>
              <div className="text-2xl font-display font-semibold text-havn-green-900">
                {totals.meals}
              </div>
              <div className="text-xs text-havn-gray-500 uppercase tracking-wide">Meals</div>
            </div>
          </div>

          <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-havn-cream-300">
            <Soup className="w-5 h-5 text-amber-600" />
            <div>
              <div className="text-2xl font-display font-semibold text-havn-green-900">
                {totals.sides}
              </div>
              <div className="text-xs text-havn-gray-500 uppercase tracking-wide">Sides</div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Auto-save status */}
          {lastSaved && !hasChanges && (
            <span className="text-xs text-havn-gray-400 flex items-center gap-1">
              <Check className="w-3 h-3 text-green-500" />
              Saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
          {saving && (
            <span className="text-xs text-havn-gray-400 flex items-center gap-1">
              <RefreshCw className="w-3 h-3 animate-spin" />
              Saving...
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`btn-primary ${!hasChanges ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {hasChanges ? 'Save Now' : 'Saved'}
          </button>
        </div>
      </div>

      {/* Meals Section */}
      {groupedDishes.meals?.length > 0 && (
        <div className="mb-8">
          <h3 className="font-display text-lg font-semibold text-havn-green-900 mb-4">
            Meals
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedDishes.meals.map(dish => (
              <MealCountCard
                key={dish.dish_id}
                dish={dish}
                count={mealCounts[dish.dish_id] || 0}
                onChange={(count) => handleCountChange(dish.dish_id, count)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Sides Section */}
      {groupedDishes.sides?.length > 0 && (
        <div>
          <h3 className="font-display text-lg font-semibold text-havn-green-900 mb-4">
            Sides
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedDishes.sides.map(dish => (
              <MealCountCard
                key={dish.dish_id}
                dish={dish}
                count={mealCounts[dish.dish_id] || 0}
                onChange={(count) => handleCountChange(dish.dish_id, count)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {(!groupedDishes.meals?.length && !groupedDishes.sides?.length) && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-havn-cream-200 flex items-center justify-center">
            <Utensils className="w-8 h-8 text-havn-gray-400" />
          </div>
          <h3 className="font-display text-lg font-semibold text-havn-gray-500">No Dishes in Menu</h3>
          <p className="text-sm text-havn-gray-400 mt-1">
            Add dishes to this menu using the Menu Builder
          </p>
        </div>
      )}

      {/* Kitchen Production Sheet — Airtable Import */}
      <ProductionSheet onImportMealCounts={handleImportMealCounts} />
    </div>
  )
}
